/** At most one module-list refresh a minute, however many refusals arrive. */
export const MODULE_UNAVAILABLE_REFRESH_COOLDOWN_MS = 60_000;

export interface ModuleUnavailableRefreshDeps {
  /** Re-derives the session's module list from Postgres (`POST /refresh`). */
  readonly refreshSession: () => Promise<unknown>;
  /** Re-reads `/me`, which serves the list from the session. */
  readonly reloadMe: () => Promise<unknown>;
  readonly now?: () => number;
}

/**
 * The shell's answer to a `module_unavailable` refusal (ADR-0100, AC-10): the
 * session still lists a module the workspace can no longer use, so refresh the
 * session FIRST and then reload `/me` — `/me` alone would re-read the same
 * stale list (see `ME_QUERY_KEY`).
 *
 * Single-flight, with a cooldown that starts when an attempt starts: a screen
 * that fires five reads at an off module gets one refresh, and a refusal that
 * keeps arriving (a module that is off for good) costs one refresh a minute.
 *
 * Failures are swallowed, like the keep-alive's: `/me` owns the verdict on
 * whether the session is gone, and the refusal itself still reaches the caller.
 */
export function createModuleUnavailableRefresh(
  deps: ModuleUnavailableRefreshDeps,
): () => void {
  const now = deps.now ?? Date.now;
  let inFlight = false;
  let lastStartedAt: number | null = null;

  return () => {
    if (inFlight) return;
    const startedAt = now();
    if (
      lastStartedAt !== null &&
      startedAt - lastStartedAt < MODULE_UNAVAILABLE_REFRESH_COOLDOWN_MS
    ) {
      return;
    }
    inFlight = true;
    lastStartedAt = startedAt;
    void deps
      .refreshSession()
      .then(() => deps.reloadMe())
      .catch(() => {
        // Intentionally silent — see above.
      })
      .finally(() => {
        inFlight = false;
      });
  };
}
